import type { MarketingSource } from '@/server/services/websiteSettings'

import { SQL, type SQLFragment } from '@/client/query'
import type { DigestEmailDetails } from '@/client/services/digest/types'
import type { KeyClubTutorathonLeaderboards } from '@/client/services/keyClub'
import type { UserAdminMessageTemplate } from '@/client/services/userAdminMessage'
import {
  KEY_DIGEST_EMAIL_DETAILS,
  KEY_FEATURED_CERTIFIED_TUTORS,
  KEY_FEATURED_SESSIONS,
  KEY_INELIGIBLE_PSAT_CWIDS,
  KEY_KEY_CLUB_TUTORATHON_2023_LEADERBOARDS,
  KEY_MARKETING_SOURCES,
  KEY_PUBLIC_COUNT_LEARNERS,
  KEY_PUBLIC_COUNT_MEMBERS,
  KEY_PUBLIC_COUNT_SESSIONS,
  KEY_PUBLIC_COUNT_TUTORS,
  KEY_REQUIRE_REGISTRATION_ACCOUNT_APPROVAL,
  KEY_SESSION_AUDIT_TAGS,
  KEY_USER_ADMIN_MESSAGE_TEMPLATES,
} from '@/client/services/websiteSettings'

const WebsiteSettingsQ = {
  ...SQL`"website_settings"`,
  key: SQL`"website_settings"."key"`,
  value: SQL`"website_settings"."value"`,

  where: <T>({ key }: { key: string }): SQLFragment<T> => SQL`
      FROM ${WebsiteSettingsQ}
      WHERE ${WebsiteSettingsQ.key} = ${key}
    `,

  getFeaturedSessions: (): SQLFragment<number[]> => SQL`
      jsonb_array_elements(${WebsiteSettingsQ.value})::int
      ${WebsiteSettingsQ.where({
        key: KEY_FEATURED_SESSIONS,
      })}`,

  getRequireRegistrationAccountApproval: (): SQLFragment<boolean> => SQL`
      ${WebsiteSettingsQ.value}
      ${WebsiteSettingsQ.where({
        key: KEY_REQUIRE_REGISTRATION_ACCOUNT_APPROVAL,
      })}`,

  getFeaturedCertifiedTutors: (): SQLFragment<number[]> => SQL`
      jsonb_array_elements(${WebsiteSettingsQ.value})::int
      ${WebsiteSettingsQ.where({
        key: KEY_FEATURED_CERTIFIED_TUTORS,
      })}`,

  getDigestEmailDetails: (): SQLFragment<DigestEmailDetails> => SQL`
      ${WebsiteSettingsQ.value}
      ${WebsiteSettingsQ.where({
        key: KEY_DIGEST_EMAIL_DETAILS,
      })}`,

  auditTagName: SQL<string>`audit_tag__name`,

  getSessionTags: (): SQLFragment<string[]> => SQL`
      FROM (SELECT jsonb_array_elements(${WebsiteSettingsQ.value}) as ${
        WebsiteSettingsQ.auditTagName
      }
      ${WebsiteSettingsQ.where({
        key: KEY_SESSION_AUDIT_TAGS,
      })}) as tags`,

  getKeyClubTutorathonLeaderboards: (): SQLFragment<KeyClubTutorathonLeaderboards> => SQL`
    ${WebsiteSettingsQ.value}
    ${WebsiteSettingsQ.where({ key: KEY_KEY_CLUB_TUTORATHON_2023_LEADERBOARDS })}`,

  getUserAdminMessageTemplates: (): SQLFragment<UserAdminMessageTemplate[]> => SQL`
    ${WebsiteSettingsQ.value} ${WebsiteSettingsQ.where({ key: KEY_USER_ADMIN_MESSAGE_TEMPLATES })}
  `,

  getPublicCountTutors: (): SQLFragment<number | null> =>
    SQL`${WebsiteSettingsQ.value} ${WebsiteSettingsQ.where({
      key: KEY_PUBLIC_COUNT_TUTORS,
    })}`,

  getPublicCountLearners: (): SQLFragment<number | null> =>
    SQL`${WebsiteSettingsQ.value} ${WebsiteSettingsQ.where({
      key: KEY_PUBLIC_COUNT_LEARNERS,
    })}`,
  getPublicCountSessions: (): SQLFragment<number | null> =>
    SQL`${WebsiteSettingsQ.value} ${WebsiteSettingsQ.where({
      key: KEY_PUBLIC_COUNT_SESSIONS,
    })}`,
  getPublicCountMembers: (): SQLFragment<number | null> =>
    SQL`${WebsiteSettingsQ.value} ${WebsiteSettingsQ.where({
      key: KEY_PUBLIC_COUNT_MEMBERS,
    })}`,

  getIneligiblePSATTutors: (): SQLFragment<string[] | null> =>
    SQL`jsonb_array_elements_text(${WebsiteSettingsQ.value}) ${WebsiteSettingsQ.where({
      key: KEY_INELIGIBLE_PSAT_CWIDS,
    })}`,

  getMarketingSources: (): SQLFragment<MarketingSource[]> => SQL`
    ${WebsiteSettingsQ.value} ${WebsiteSettingsQ.where({ key: KEY_MARKETING_SOURCES })}
  `,
}

export default WebsiteSettingsQ
